import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../common/SEOHead';
import SafeIcon from '../common/SafeIcon';

const {FiUsers, FiSearch, FiCheckCircle, FiFileText, FiHome, FiShield, FiAward, FiClock, FiDollarSign} = FiIcons;

const TenantPlacementPage = () => {
  const screeningCriteria = [
    {
      icon: FiDollarSign,
      title: 'Financial Verification',
      description: 'Comprehensive income verification ensuring tenants can comfortably afford rental payments.',
      checks: ['Income verification (2.5x rent)', 'Employment confirmation', 'Credit score analysis', 'Rental payment history']
    },
    {
      icon: FiShield,
      title: 'Background Screening',
      description: 'Thorough background checks to ensure tenant reliability and property safety.',
      checks: ['Criminal background check', 'Reference verification', 'Previous landlord feedback', 'Identity verification']
    },
    {
      icon: FiHome,
      title: 'Rental History Assessment',
      description: 'Detailed review of past rental behavior and property maintenance records.',
      checks: ['Eviction history check', 'Property maintenance record', 'Neighbor complaints review', 'Lease compliance history']
    },
    {
      icon: FiFileText,
      title: 'Legal Compliance',
      description: 'All screening processes comply with Alberta Residential Tenancies Act and fair housing laws.',
      checks: ['Fair housing compliance', 'Documentation standards', 'Privacy protection', 'Legal requirement adherence']
    }
  ];

  const benefits = [
    '90% Reduction in Evictions',
    '30% Longer Tenant Retention',
    '50% Faster Property Vacancy Filling',
    '24-Hour Application Processing',
    'Professional Showings',
    'Comprehensive Lease Preparation'
  ];

  const process = [
    { step: '1', title: 'Property Marketing', description: 'Professional photography, compelling listings, and targeted advertising across major rental platforms.' },
    { step: '2', title: 'Application Management', description: 'Streamlined application process with online forms and rapid response to qualified applicants.' },
    { step: '3', title: 'Thorough Screening', description: 'Multi-point screening process including credit, background, income, and reference verification.' },
    { step: '4', title: 'Lease Execution', description: 'Professional lease preparation and signing with proper documentation and move-in coordination.' }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Tenant Placement Services Calgary",
    "description": "Professional tenant placement services in Calgary with comprehensive screening, marketing, and lease preparation to find quality tenants quickly.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "DGM Property Management",
      "telephone": "587-800-1215",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Calgary",
        "addressRegion": "Alberta",
        "addressCountry": "CA"
      }
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "51.0447",
        "longitude": "-114.0719"
      },
      "geoRadius": "50000"
    },
    "serviceType": ["Tenant Placement", "Tenant Screening", "Rental Marketing", "Lease Preparation", "Background Checks"]
  };

  return (
    <>
      <SEOHead
        title="Tenant Placement Services Calgary | Professional Tenant Screening"
        description="DGM Property Management offers expert tenant placement in Calgary. Comprehensive screening, professional marketing, and quality tenant selection. 90% fewer evictions. Call 587-800-1215."
        keywords="tenant placement Calgary, tenant screening Calgary, rental property tenants Calgary, find tenants Calgary, tenant background checks, Calgary rental management"
        canonical="/services/tenant-placement"
        ogImage="https://images.unsplash.com/photo-1570129477492-45c003edd2be?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630"
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Professional Tenant Placement Services
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Find quality tenants quickly with our comprehensive screening process. 
                90% fewer evictions and 30% longer tenant retention through professional tenant selection and placement.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Screening Criteria Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Tenant Screening</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our multi-point screening process ensures only the most qualified tenants are placed in your Calgary property.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {screeningCriteria.map((criteria, index) => (
                <motion.div
                  key={criteria.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={criteria.icon} className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-3">{criteria.title}</h3>
                      <p className="text-gray-600 leading-relaxed mb-4">{criteria.description}</p>
                      <div className="space-y-2">
                        {criteria.checks.map((check, checkIndex) => (
                          <div key={checkIndex} className="flex items-center gap-2">
                            <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-gray-700">{check}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Tenant Placement Process</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                A systematic approach to finding and placing quality tenants in your Calgary property.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-4 gap-8">
              {process.map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold text-gray-900 mb-8">Why Choose Our Tenant Placement?</h2>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={benefit}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <SafeIcon icon={FiAward} className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      <span className="text-lg text-gray-700">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="relative overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1570129477492-45c003edd2be?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
                    alt="Professional tenant placement and screening services"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-4">Find Quality Tenants for Your Calgary Property</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Our expert tenant placement services fill vacancies 50% faster with tenants who stay 30% longer. 
                Contact us today for professional tenant screening and placement.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Start Tenant Placement
                </motion.a>
                <motion.a
                  href="tel:587-800-1215"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Call 587-800-1215
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TenantPlacementPage;